import {
  Button,
  Col,
  DatePicker,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useCallback, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  IncidentFollowUp,
  IncidentFollowUpStatus,
} from "../../repository/data.default";
import {
  addIncidentFollowUp,
  deleteIncidentFollowUp,
  getIncidentFollowUps,
  updateIncidentFollowUp,
} from "../../repository/data.repository";
import { getProblemMessage } from "../../utils/http";
import { QueryOperation } from "../../code/search-query/search-query";

// PD-01 §11 — follow-up actions panel inlined on the Incident detail dialog.
// Lists outstanding and completed actions, lets the operator add new ones,
// edit inline, and remove. Status change to Completed stamps CompletedAt
// server-side (see IncidentFollowUpsController.Put).

export interface IncidentFollowUpsPanelProps {
  readonly incidentUid?: string;
  readonly canEdit?: boolean;
}

const statusColor: Record<IncidentFollowUpStatus, string> = {
  [IncidentFollowUpStatus.Open]: "default",
  [IncidentFollowUpStatus.InProgress]: "blue",
  [IncidentFollowUpStatus.Completed]: "green",
  [IncidentFollowUpStatus.Cancelled]: "red",
};

function IncidentFollowUpsPanel(props: IncidentFollowUpsPanelProps) {
  const [items, setItems] = useState<IncidentFollowUp[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newAction, setNewAction] = useState("");
  const [newDue, setNewDue] = useState<Dayjs | null>(null);

  const load = useCallback(() => {
    if (!props.incidentUid) {
      setItems([]);
      return;
    }
    setLoading(true);
    getIncidentFollowUps({
      searchQuery: {
        conditions: [
          {
            field: "incidentUid",
            values: [props.incidentUid],
            operation: QueryOperation.Equal,
          },
        ],
      },
      pageNumber: 0,
      pageSize: 100,
      sortBy: "createdOn",
      sortDescending: false,
    })
      .then(({ data }) => setItems(data))
      .catch((err) => message.error(getProblemMessage(err) || "Failed to load follow-ups."))
      .finally(() => setLoading(false));
  }, [props.incidentUid]);

  useEffect(() => {
    load();
  }, [load]);

  function onAdd() {
    if (!props.incidentUid || !newAction.trim()) return;
    setAdding(true);
    addIncidentFollowUp({
      incidentUid: props.incidentUid,
      action: newAction.trim(),
      dueDate: newDue ? newDue.toISOString() : null,
      status: IncidentFollowUpStatus.Open,
    })
      .then(() => {
        setNewAction("");
        setNewDue(null);
        load();
      })
      .catch((err) => message.error(getProblemMessage(err) || "Failed to add follow-up."))
      .finally(() => setAdding(false));
  }

  function onStatusChange(row: IncidentFollowUp, status: IncidentFollowUpStatus) {
    updateIncidentFollowUp({
      uid: row.uid,
      rowVersion: row.rowVersion,
      action: row.action,
      ownerUid: row.ownerUid,
      dueDate: row.dueDate,
      evidence: row.evidence,
      status,
    })
      .then(load)
      .catch((err) => message.error(getProblemMessage(err) || "Failed to update follow-up."));
  }

  function onRemove(row: IncidentFollowUp) {
    Modal.confirm({
      title: "Remove this follow-up action?",
      content: row.action,
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: () =>
        deleteIncidentFollowUp(row.uid)
          .then(load)
          .catch((err) => message.error(getProblemMessage(err) || "Failed to remove.")),
    });
  }

  const columns: ColumnsType<IncidentFollowUp> = [
    {
      title: "Action",
      dataIndex: "action",
      ellipsis: true,
    },
    {
      title: "Owner",
      dataIndex: "ownerUserName",
      width: 160,
      render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: "Due",
      dataIndex: "dueDate",
      width: 120,
      render: (v: string | null) =>
        v ? dayjs(v).format("DD/MM/YYYY") : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 160,
      render: (status: IncidentFollowUpStatus, row) =>
        props.canEdit ? (
          <Select
            size="small"
            value={status}
            style={{ width: 140 }}
            options={Object.values(IncidentFollowUpStatus).map((s) => ({
              label: s,
              value: s,
            }))}
            onChange={(v) => onStatusChange(row, v)}
          />
        ) : (
          <Tag color={statusColor[status]}>{status}</Tag>
        ),
    },
    {
      title: "",
      key: "actions",
      width: 56,
      render: (_, row) =>
        props.canEdit ? (
          <Button size="small" danger type="text" onClick={() => onRemove(row)}>
            Delete
          </Button>
        ) : null,
    },
  ];

  return (
    <Col className="flex flex-col gap-3">
      <Typography.Text strong>Follow-Up Actions</Typography.Text>
      <Table
        rowKey="uid"
        size="small"
        loading={loading}
        dataSource={items}
        columns={columns}
        pagination={false}
        locale={{ emptyText: "No follow-up actions yet." }}
      />
      {props.canEdit && (
        <Row align="middle" gutter={8}>
          <Col flex="auto">
            <Input
              placeholder="Describe the next action to take"
              value={newAction}
              onChange={(e) => setNewAction(e.target.value)}
              onPressEnter={onAdd}
            />
          </Col>
          <Col>
            <DatePicker
              value={newDue}
              onChange={(v) => setNewDue(v)}
              placeholder="Due date"
            />
          </Col>
          <Col>
            <Space>
              <Button
                type="primary"
                loading={adding}
                disabled={!newAction.trim()}
                onClick={onAdd}
              >
                Add
              </Button>
            </Space>
          </Col>
        </Row>
      )}
    </Col>
  );
}

export default IncidentFollowUpsPanel;
